import callApi, { ApiCallOptions } from './callApi';
import { CheckoutState, DeliveryOption } from './checkoutState';
import { getInitialStep, Step } from './step';
import { getAddressForSubmission } from './streetHouseNumber';
import { getGaEcommerceObject, getPriceBi, pushTrackingEvent } from './tracking';

const getMutations = (currentStep: Step, setCheckoutState: (newState: CheckoutState) => void) => {
  const runMutation = async (options: ApiCallOptions) => {
    let newState: CheckoutState;
    // For certain mutations the new V3 endpoints don't exist yet, therefore we have to use the "legacy" endpoints
    // to trigger the operation and get the updated state in a second call
    if (options.flow === 'legacy') {
      await callApi(options);
      newState = await callApi<CheckoutState>({ endpoint: '/state', failedStatusCodes: [204], sequential: true });
    } else {
      newState = await callApi<CheckoutState>(options);
    }
    setCheckoutState(newState);
    return newState;
  };

  return {
    signIn: async (payload: { email: string; password: string }) => {
      const newState = await runMutation({ endpoint: '/customer/login', payload, flow: 'legacy', sequential: true });
      pushTrackingEvent({
        event: 'login',
        method: 'email',
        status: 'successful',
        customer_id: newState.customer?.data.id,
        eh: newState.customer?.data.trackingHash,
      });
      return getInitialStep(newState);
    },
    register: async (payload: {
      gender: string;
      firstName: string;
      lastName: string;
      email: string;
      password?: string;
      hasSubscribedNewsletter?: boolean;
      source: string;
    }) => {
      const newState = await runMutation({ endpoint: '/customer', payload, flow: 'legacy', sequential: true });
      pushTrackingEvent({
        event: 'register',
        method: 'email',
        status: 'successful',
        customer_id: newState.customer?.data.id,
        eh: newState.customer?.data.trackingHash,
      });
    },
    setBasketItemQuantity: async (
      { id, quantity }: { id: string; quantity: number },
      event: 'add_to_cart' | 'remove_from_cart'
    ) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: `/state/basket/items/${id}`,
        payload: { quantity },
        allowedStatusCodes: [404, 406],
        failedStatusCodes: [204],
        sequential: true,
      });
      pushTrackingEvent({
        event,
        action: `${currentStep}|${id}`,
        price_bi: getPriceBi(newState.orderState.basket, id),
        // master_key: productMasterKey,
        ecommerce: getGaEcommerceObject(newState),
      });
    },
    removeBasketItem: async ({ id }: { id: string }) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: `/state/basket/items/${id}`,
        payload: { quantity: 0 },
        allowedStatusCodes: [404, 406],
        failedStatusCodes: [204],
        sequential: true,
      });
      pushTrackingEvent({
        event: 'remove_from_cart',
        action: `${currentStep}|${id}`,
        price_bi: getPriceBi(newState.orderState.basket, id),
        // master_key: productMasterKey,
        ecommerce: getGaEcommerceObject(newState),
      });
    },
    setCarrier: async (payload: Pick<DeliveryOption, 'carrierKey' | 'carrierGroupKey' | 'shippingOptionKey'>) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: '/state/order/delivery/option',
        payload: { data: { deliveryOptionKey: 'home_delivery', ...payload } },
        sequential: true,
      });
      pushTrackingEvent({
        event: 'add_shipping_info',
        shipping_type: newState.orderState.shipping?.selected?.[0].carrierKey,
        shipping_method: 'carrier',
        ecommerce: getGaEcommerceObject(newState),
      });
    },
    setCollectionPoint: async ({
      specification,
      type: carrier,
    }: {
      specification: {
        collectionPointId: string;
        collectionPointCustomerNumber?: string;
        customer: { firstName: string; lastName: string };
      };
      type: Pick<DeliveryOption, 'carrierKey' | 'carrierGroupKey' | 'shippingOptionKey' | 'collectionPointType'>;
    }) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: '/state/order/delivery/option',
        sequential: true,
        payload: {
          data: { deliveryOptionKey: 'collection_points', ...carrier },
          specification,
        },
      });
      pushTrackingEvent({
        event: 'add_shipping_info',
        shipping_type: 'collectionPoint',
        shipping_method: newState.orderState.shipping?.selected?.[0].collectionPointType,
        ecommerce: getGaEcommerceObject(newState),
      });
    },
    setShippingAddress: async (payload: ReturnType<typeof getAddressForSubmission>) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: '/state/order/addresses/shipping',
        payload,
        sequential: true,
        allowedStatusCodes: [422],
      });
      return newState.addressSuggestions?.suggestions;
    },
    setShippingAddressAsBillingAddress: async () => {
      await runMutation({ endpoint: '/state/order/addresses/copy/shipping/to/billing', payload: {}, sequential: true });
    },
    setBillingAddress: async (payload: ReturnType<typeof getAddressForSubmission>) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: '/state/order/addresses/billing',
        payload,
        sequential: true,
        allowedStatusCodes: [422],
      });
      return newState.addressSuggestions?.suggestions;
    },
    setDynamicFields: async (payload: {
      customer?: { birthDate?: string; phone?: string; carrierPhone?: string; hasSubscribedNewsletter?: boolean };
      order?: { taxId?: string };
    }) => {
      await runMutation({ method: 'PATCH', endpoint: '/state', payload, sequential: true });
    },
    setPaymentMethod: async (payload: { paymentOptionKey: string; recurringPaymentOptionId?: string }) => {
      const newState = await runMutation({
        endpoint: '/state/order/payment/option',
        payload: { data: payload },
        sequential: true,
      });
      pushTrackingEvent({
        event: 'payment_method',
        action: 'select',
        label: newState.orderState.payment?.selected?.[0].paymentOptionKey,
      });
      pushTrackingEvent({
        event: 'add_payment_info',
        ecommerce: getGaEcommerceObject(newState),
        payment_type: newState.orderState.payment?.selected?.[0].paymentOptionKey,
      });
    },
    updatePaymentMethod: async ({
      paymentOptionKey,
      specification,
    }: {
      paymentOptionKey: string;
      specification?: any;
    }) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: '/state/order/payment/option',
        payload: { data: { paymentOptionKey }, specification },
        sequential: true,
      });
      pushTrackingEvent({
        event: 'payment_method',
        action: 'select',
        label: newState.orderState.payment?.selected?.[0].paymentOptionKey,
      });
      pushTrackingEvent({
        event: 'add_payment_info',
        ecommerce: getGaEcommerceObject(newState),
        payment_type: newState.orderState.payment?.selected?.[0].paymentOptionKey,
      });
    },
    addGiftCard: async (payload: { giftCardCode: string; giftCardPin: string }) => {
      const newState = await runMutation({
        endpoint: '/payment/customer/giftcard',
        payload: { asPaymentMethod: true, ...payload },
        flow: 'legacy',
      });
      pushTrackingEvent({
        event: 'gift_card',
        action: 'add',
        label: null,
        value: null,
      });
      return newState.orderState.payment?.giftCards;
    },
    removeGiftCard: async ({ id }: { id: string }) => {
      await runMutation({ method: 'DELETE', endpoint: `/state/order/payment/recurring/${id}` });
      pushTrackingEvent({
        event: 'gift_card',
        action: 'remove',
        label: null,
        value: null,
      });
    },
    addVoucher: async (payload: { code: string }) => {
      const {
        orderState: { vouchers = [] },
      } = await runMutation({ endpoint: '/voucher', payload, flow: 'legacy', sequential: true });
      pushTrackingEvent({
        event: 'coupon',
        action: 'add',
        label: payload.code,
        value: null,
      });
      return vouchers;
    },
    removeVoucher: async ({ id }: { id: string }) => {
      await runMutation({ method: 'DELETE', endpoint: `/voucher/${id}`, flow: 'legacy', sequential: true });
      pushTrackingEvent({
        event: 'coupon',
        action: 'remove',
        label: id,
        value: null,
      });
    },
    removeRecurringPaymentMethod: async ({ id }: { id: string }) => {
      await runMutation({ method: 'DELETE', endpoint: `/payment/recurring/${id}`, flow: 'legacy', sequential: true });
    },
    setLoyaltyProgram: async (payload: { cardKey: string; type: string }) => {
      await runMutation({ endpoint: '/customer/membershipCard', payload, flow: 'legacy', sequential: true });
      pushTrackingEvent({ event: 'payback', action: 'add', label: null, value: payload.cardKey });
    },
    removeLoyaltyProgram: async ({ cardKey, type }: { cardKey: string; type: string }) => {
      await runMutation({
        method: 'DELETE',
        endpoint: `/customer/membershipCard/${type}/${cardKey}`,
        flow: 'legacy',
        sequential: true,
      });
      pushTrackingEvent({ event: 'payback', action: 'remove', label: null, value: cardKey });
    },
    confirmOrder: async ({
      paymentOptionKey,
      recurringPaymentOptionId,
      currentUrl,
      specification,
    }: {
      paymentOptionKey: string;
      recurringPaymentOptionId?: string;
      currentUrl: string;
      specification?: {
        key?: string; // needed for adyen_ideal
        iban?: string; // needed for ratepay_directdebit
        paymentMethod?: Record<string, unknown>; // needed for adyen_creditcard
        browserInfo?: Record<string, unknown>; // needed for adyen_creditcard
      };
    }) => {
      const newState = await runMutation({
        endpoint: '/state/order/confirmation/execute',
        payload: {
          data: { paymentOptionKey, recurringPaymentOptionId },
          specification,
          referer: currentUrl,
        },
        allowedStatusCodes: [402, 406, 422],
        sequential: true,
      });
      pushTrackingEvent({
        event: 'complete_checkout',
        action: `${paymentOptionKey}|${newState.orderState.id}|${newState.customer?.data.id}`,
        label: null,
      });
    },
  };
};

export default getMutations;
